'use client'

import { ArrowLeft, Printer } from 'lucide-react'
import Link from 'next/link'
import { useEffect } from 'react'
import { emailLink, routePaths } from '@/shared/config/site'
import { Button } from '@/shared/ui/Button'
import { Separator } from '@/shared/ui/Separator'

export function ResumePage() {
  useEffect(() => {
    document.title = 'Resume | Vitaliy'
  }, [])

  return (
    <div className="page-shell px-5 py-24 md:px-8 lg:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="print-hidden mb-8 flex flex-wrap items-center justify-between gap-4">
          <Button asChild size="sm" variant="ghost">
            <Link href={routePaths.home}>
              <ArrowLeft className="h-4 w-4" />
              {'Back Home'}
            </Link>
          </Button>
          <div className="flex flex-wrap gap-3">
            <Button size="sm" variant="secondary" onClick={() => window.print()}>
              <Printer className="h-4 w-4" />
              {'Print'}
            </Button>
          </div>
        </div>
        <article className="resume-print-surface p-8 sm:p-10 lg:p-12">
          <header>
            <p className="text-sm font-medium text-slate-600">{'Vitaliy'}</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-950 sm:text-5xl">{'Senior Software Engineer'}</h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-700">
              {
                'Hire an autist when the engineering problem is ugly, high-stakes, politically annoying, and too complex for vague thinking. Currently building a local AI execution runtime for autonomous teams, with deep hands-on work in agent tooling, long-running coordinators, and high-performance systems that do not get to fail once they are real.'
              }
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
              <a className="underline decoration-slate-300 underline-offset-4" href={emailLink.href}>
                {emailLink.label}
              </a>
            </div>
          </header>
          <Separator className="my-10 bg-slate-300/80" />
          <section>
            <h2 className="text-base font-semibold text-slate-900">{'Technical range'}</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {[
                {
                  title: 'AI / LLM',
                  items: [
                    'Multi-agent orchestration',
                    'Vectorized knowledge bases',
                    'Autonomous runtimes',
                    'Tool-use architectures',
                    'Model routing',
                    'Persistent Memory',
                    'RAG and MCP',
                  ],
                },
                {
                  items: ['Rust', 'TypeScript', 'Python', 'Node.js', 'React', 'Next.js', 'React Native'],
                  title: 'Languages',
                },
                {
                  items: ['PostgreSQL', 'SurrealDB', 'MongoDB', 'Redis', 'RabbitMQ'],
                  title: 'Data',
                },
                {
                  items: ['Docker', 'AWS', 'Google Cloud', 'GitHub Actions', 'CI/CD'],
                  title: 'Infrastructure',
                },
              ].map((group) => (
                <div key={group.title}>
                  <h3 className="text-sm font-semibold text-slate-900">{group.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-700">{group.items.join(', ')}</p>
                </div>
              ))}
            </div>
          </section>
          <Separator className="my-10 bg-slate-300/80" />
          <section>
            <h2 className="text-base font-semibold text-slate-900">{'Experience'}</h2>
            <div className="mt-5 space-y-7">
              {[
                {
                  company: 'blprnt',
                  highlights: ['Local AI execution runtime', 'Rust coordinator', 'Agent orchestration'],
                  id: 'blprnt',
                  period: 'Aug 2025 – Present',
                  role: 'Founder / Lead Engineer',
                },
                {
                  company: 'The Fitting Room',
                  highlights: ['React Native', 'Next.js dashboard', '90% faster onboarding workflows'],
                  id: 'fitting-room',
                  period: 'Sep 2023 – Nov 2025',
                  role: 'Senior Software Engineer',
                },
                {
                  company: 'Independent Software Development',
                  highlights: ['Real-time pipelines', 'Trading systems', 'Rust and React delivery'],
                  id: 'independent-software-development',
                  period: 'Jun 2022 – Sep 2023',
                  role: 'Software Engineer',
                },
                {
                  company: 'Valstro',
                  highlights: ['Low-latency backend services', '50% latency reduction', 'Data-intensive UI'],
                  id: 'valstro',
                  period: 'Jan 2022 – Jun 2022',
                  role: 'Software Engineering Consultant',
                },
                {
                  company: 'Amazon Web Services',
                  highlights: ['React migration', 'Accessibility', 'Engineering mentorship'],
                  id: 'aws',
                  note: 'Frontend modernization and CI/CD improvements.',
                  period: 'Jul 2021 – Dec 2021',
                  role: 'Software Engineer Level 6',
                },
                {
                  company: 'Independent Consulting',
                  highlights: ['Enterprise clients', 'React + Node.js', 'Scalability and performance'],
                  id: 'independent-consulting',
                  period: 'Jan 2020 – Jul 2021',
                  role: 'Consultant',
                },
                {
                  company: 'MoneyLion',
                  highlights: ['Team of 6', '20% drop-off reduction', 'Analytics instrumentation'],
                  id: 'moneylion',
                  period: 'Jan 2019 – Jan 2020',
                  role: 'Engineering Manager',
                },
                {
                  company: 'Teachable',
                  highlights: ['Frontend guild lead', 'Build times down 87%', 'Shared standards'],
                  id: 'teachable',
                  period: 'Jul 2017 – Jan 2019',
                  role: 'Senior Frontend Engineer',
                },
                {
                  company: 'Hearst Autos',
                  highlights: ['Led 15+ engineers', 'Angular + TypeScript', 'SSR contribution lineage'],
                  id: 'hearst-autos',
                  period: 'May 2016 – Jun 2017',
                  role: 'Lead Software Engineer',
                },
                {
                  company: 'SureDone',
                  highlights: ['REST APIs', 'Scalable commerce platform', 'Performance optimization'],
                  id: 'suredone',
                  period: 'Jun 2014 – Mar 2016',
                  role: 'Senior Software Engineer',
                },
                {
                  company: 'DiscountTronic',
                  highlights: ['jQuery, PHP, MySQL', 'Linux, Apache', 'ebay API'],
                  id: 'discounttronic',
                  period: 'Jan 2007 – Jun 2014',
                  role: 'Senior Software Engineer',
                },
              ].map((entry) => (
                <article key={`${entry.company}-${entry.period}`} className="grid gap-3 md:grid-cols-[13rem_1fr]">
                  <div className="text-sm text-slate-500">{entry.period}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">{entry.company}</h3>
                    <p className="text-sm text-slate-500">{entry.role}</p>
                    {entry.note ? <p className="mt-3 text-sm text-slate-700">{entry.note}</p> : null}
                    <p className="mt-3 text-sm leading-7 text-slate-700">{entry.highlights.join(' · ')}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </article>
      </div>
    </div>
  )
}
